import { getQueryClient } from '@/providers/queryClient';
import { getList } from '@/requests/api/get-list';
import { getListNested } from '@/requests/api/get-list-nested';
import { QUERY_KEYS } from '@/requests/constants';
import type {
  Building,
  IndustrialPremises,
  IndustrialPremisesListing,
  ModelName,
  ParkingLot,
  ParkingSpot,
  ParkingSpotListing,
} from '@pigello/pigello-matrix';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';

type TSpaceListing = IndustrialPremisesListing | ParkingSpotListing;
type TSpace = IndustrialPremises | ParkingSpot;

/**
 * Builds the custom nested-case handler for a space-based listing model
 * (parkingspotlisting/industrialpremiseslisting): batch-fetches the underlying
 * spaces and their realestate (via building, or via parking lot for
 * lot-only parking spots) and attaches each as `_space` on the listing.
 */
export function createSpaceListingNestedCase(
  spaceModelName: Extract<ModelName, 'parkingspot' | 'industrialpremises'>,
  source: string
) {
  return async function handleCustomSpaceListingNestedCase(
    listResponse: TCustomList<TSpaceListing>
  ): Promise<TCustomList<TSpaceListing & { _space?: TSpace }>> {
    if (!listResponse.data.length) return listResponse;

    // Extract unique space IDs from marketSpace relations
    // MarketSpace.id === Space.id (1:1 mapping)
    const spaceIdSet = new Set<string>();
    for (const listing of listResponse.data) {
      const id = listing.marketSpace?.id;
      if (id) {
        spaceIdSet.add(id);
      }
    }

    if (spaceIdSet.size === 0) return listResponse;

    // Batch-fetch the spaces
    const queryClient = getQueryClient();
    const chunkedSpaceIds = chunkIds(spaceIdSet);

    const spacePromises = chunkedSpaceIds.map(async (chunk) =>
      queryClient.ensureQueryData({
        queryKey: [spaceModelName, QUERY_KEYS.LIST, { ids: chunk, source }],
        queryFn: () =>
          getList<TSpace>({
            modelName: spaceModelName,
            queryParams: {
              filters: {
                id: { __in: chunk.join(',') },
              },
              pageSize: chunk.length,
            },
          }),
      })
    );

    const spaceResults = await Promise.allSettled(spacePromises);

    const spaceMap = new Map<string, TSpace>();
    for (const result of spaceResults) {
      if (result.status === 'fulfilled' && result.value.list) {
        for (const space of result.value.list) {
          spaceMap.set(space.id, space);
        }
      }
    }

    // Batch-fetch buildings (with realestate nested) for the spaces
    const buildingIdSet = new Set<string>();
    for (const space of spaceMap.values()) {
      const buildingId = space.building?.id;
      if (buildingId) {
        buildingIdSet.add(buildingId);
      }
    }

    if (buildingIdSet.size > 0) {
      const chunkedBuildingIds = chunkIds(buildingIdSet);

      const buildingPromises = chunkedBuildingIds.map(async (chunk) =>
        queryClient.ensureQueryData({
          queryKey: ['building', QUERY_KEYS.LIST, { ids: chunk, source }],
          queryFn: () =>
            getListNested<Building>({
              modelName: 'building',
              nested: ['realestate'],
              queryParams: {
                filters: {
                  id: { __in: chunk.join(',') },
                },
                pageSize: chunk.length,
              },
            }),
        })
      );

      const buildingResults = await Promise.allSettled(buildingPromises);

      const buildingMap = new Map<string, Building>();
      for (const result of buildingResults) {
        if (result.status === 'fulfilled' && result.value.list) {
          for (const building of result.value.list) {
            buildingMap.set(building.id, building);
          }
        }
      }

      // Merge buildings onto the spaces
      for (const [spaceId, space] of spaceMap) {
        const buildingId = space.building?.id;
        if (buildingId) {
          const building = buildingMap.get(buildingId);
          if (building) {
            spaceMap.set(spaceId, {
              ...space,
              building: building as TSpace['building'],
            });
          }
        }
      }
    }

    // Parking spots can live on a parking lot without a building; fetch the
    // lots (with realestate nested) so those rows still resolve a realestate.
    const parkingLotIdSet = new Set<string>();
    for (const space of spaceMap.values()) {
      if (
        'parkingLot' in space &&
        space.parkingLot?.id &&
        !space.building?.id
      ) {
        parkingLotIdSet.add(space.parkingLot.id);
      }
    }

    if (parkingLotIdSet.size > 0) {
      const chunkedParkingLotIds = chunkIds(parkingLotIdSet);

      const parkingLotPromises = chunkedParkingLotIds.map(async (chunk) =>
        queryClient.ensureQueryData({
          queryKey: ['parkinglot', QUERY_KEYS.LIST, { ids: chunk, source }],
          queryFn: () =>
            getListNested<ParkingLot>({
              modelName: 'parkinglot',
              nested: ['realestate'],
              queryParams: {
                filters: {
                  id: { __in: chunk.join(',') },
                },
                pageSize: chunk.length,
              },
            }),
        })
      );

      const parkingLotResults = await Promise.allSettled(parkingLotPromises);

      const parkingLotMap = new Map<string, ParkingLot>();
      for (const result of parkingLotResults) {
        if (result.status === 'fulfilled' && result.value.list) {
          for (const parkingLot of result.value.list) {
            parkingLotMap.set(parkingLot.id, parkingLot);
          }
        }
      }

      // Merge parking lots onto the spots
      for (const [spaceId, space] of spaceMap) {
        if (!('parkingLot' in space)) continue;
        const parkingLotId = space.parkingLot?.id;
        if (!parkingLotId) continue;
        const parkingLot = parkingLotMap.get(parkingLotId);
        if (parkingLot) {
          spaceMap.set(spaceId, {
            ...space,
            parkingLot: parkingLot as ParkingSpot['parkingLot'],
          });
        }
      }
    }

    // Merge space data onto each listing
    return {
      data: listResponse.data.map((listing) => {
        const spaceId = listing.marketSpace?.id;
        return {
          ...listing,
          _space: spaceId ? spaceMap.get(spaceId) : undefined,
        };
      }),
    };
  };
}
